import React from 'react'
import { IconButton, Tooltip } from '@mui/material'
import ViewColumnIcon from '@mui/icons-material/ViewColumn'
import TableRowsIcon from '@mui/icons-material/TableRows'
import AssignmentIcon from '@mui/icons-material/Assignment'
import PersonIcon from '@mui/icons-material/Person'
import AddIcon from '@mui/icons-material/Add'
import PersonAddIcon from '@mui/icons-material/PersonAdd'
import { Package } from '../../enums/package.enum'
import { isMinimumPackage } from '../../utils/package-validator'
import { TaskBoardType } from '../../types/task-board'
import { useSelector } from 'react-redux'
import { State } from '../../types/state'

interface TaskBoardIconsProps {
  viewMode: 'columns' | 'table'
  filterMode: 'all' | 'assigned'
  board: TaskBoardType | null
  userPackage: Package | null
  packageExpiredAt: Date | string | null
  packageUsersNumber?: number
  onViewModeChange: (mode: 'columns' | 'table') => void
  onFilterModeChange: (mode: 'all' | 'assigned') => void
  onInviteClick: () => void
  onAddColumnClick: () => void
}

export const TaskBoardIcons: React.FC<TaskBoardIconsProps> = ({
  viewMode,
  filterMode,
  board,
  userPackage,
  packageExpiredAt,
  packageUsersNumber,
  onViewModeChange,
  onFilterModeChange,
  onInviteClick,
  onAddColumnClick
}) => {
  const userState = useSelector((state: State) => state.user)
  const isOwner = board?.members.some(member => member.id === userState.id?.toString() && member.role === 'owner')

  const rightIcons: JSX.Element[] = [
    <Tooltip title='Widok kolumnowy' key='columns'>
      <IconButton
        color={viewMode === 'columns' ? 'primary' : 'default'}
        onClick={() => onViewModeChange('columns')}
        sx={{
          transition: 'all 0.2s ease-in-out',
          borderRadius: '8px',
          padding: '8px',
          position: 'relative',
          '&::after': viewMode === 'columns'
            ? {
                content: '""',
                position: 'absolute',
                bottom: '-2px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '20px',
                height: '3px',
                backgroundColor: '#47a145',
                borderRadius: '2px'
              }
            : {},
          '& .MuiSvgIcon-root': {
            color: '#47a145',
            transition: 'color 0.2s ease-in-out'
          }
        }}
      >
        <ViewColumnIcon />
      </IconButton>
    </Tooltip>,
    <Tooltip title='Widok tabelaryczny' key='table'>
      <IconButton
        color={viewMode === 'table' ? 'primary' : 'default'}
        onClick={() => onViewModeChange('table')}
        sx={{
          transition: 'all 0.2s ease-in-out',
          borderRadius: '8px',
          padding: '8px',
          position: 'relative',
          '&::after': viewMode === 'table'
            ? {
                content: '""',
                position: 'absolute',
                bottom: '-2px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '20px',
                height: '3px',
                backgroundColor: '#47a145',
                borderRadius: '2px'
              }
            : {},
          '& .MuiSvgIcon-root': {
            color: '#47a145',
            transition: 'color 0.2s ease-in-out'
          }
        }}
      >
        <TableRowsIcon />
      </IconButton>
    </Tooltip>,
    <Tooltip title='Wszystkie zadania' key='all'>
      <IconButton
        color={filterMode === 'all' ? 'primary' : 'default'}
        onClick={() => onFilterModeChange('all')}
        sx={{
          transition: 'all 0.2s ease-in-out',
          borderRadius: '8px',
          padding: '8px',
          position: 'relative',
          '&::after': filterMode === 'all'
            ? {
                content: '""',
                position: 'absolute',
                bottom: '-2px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '20px',
                height: '3px',
                backgroundColor: '#47a145',
                borderRadius: '2px'
              }
            : {},
          '& .MuiSvgIcon-root': {
            color: '#47a145',
            transition: 'color 0.2s ease-in-out'
          }
        }}
      >
        <AssignmentIcon />
      </IconButton>
    </Tooltip>,
    <Tooltip title='Przypisane do mnie' key='assigned'>
      <IconButton
        color={filterMode === 'assigned' ? 'primary' : 'default'}
        onClick={() => onFilterModeChange('assigned')}
        sx={{
          transition: 'all 0.2s ease-in-out',
          borderRadius: '8px',
          padding: '8px',
          position: 'relative',
          '&::after': filterMode === 'assigned'
            ? {
                content: '""',
                position: 'absolute',
                bottom: '-2px',
                left: '50%',
                transform: 'translateX(-50%)',
                width: '20px',
                height: '3px',
                backgroundColor: '#47a145',
                borderRadius: '2px'
              }
            : {},
          '& .MuiSvgIcon-root': {
            color: '#47a145',
            transition: 'color 0.2s ease-in-out'
          }
        }}
      >
        <PersonIcon />
      </IconButton>
    </Tooltip>,
    ...(isMinimumPackage(userPackage, Package.PREMIUM, packageExpiredAt instanceof Date ? packageExpiredAt : packageExpiredAt ? new Date(packageExpiredAt) : null)
      ? [
        <Tooltip title={isOwner ? 'Zarządzaj zespołem' : 'Tylko właściciel może zarządzać zespołem'} key='invite'>
          <span>
            <IconButton
              color='primary'
              onClick={onInviteClick}
              disabled={!isOwner}
              sx={{
                '&:hover': {
                  backgroundColor: 'rgba(25, 118, 210, 0.08)'
                },
                '&.Mui-disabled': {
                  backgroundColor: '#e0e0e0',
                  color: 'rgba(0, 0, 0, 0.26)'
                }
              }}
            >
              <PersonAddIcon />
            </IconButton>
          </span>
        </Tooltip>
        ]
      : []),
    <Tooltip title='Dodaj kolumnę lub zadanie' key='add'>
      <IconButton
        color='primary'
        onClick={onAddColumnClick}
        sx={{
          '&:hover': {
            backgroundColor: 'rgba(25, 118, 210, 0.08)'
          },
          '& .MuiSvgIcon-root': {
            color: '#47a145'
          }
        }}
      >
        <AddIcon />
      </IconButton>
    </Tooltip>
  ]

  return (
    <div
      style={{
        display: 'flex',
        flexWrap: window.innerWidth <= 600 ? 'wrap' : 'nowrap',
        justifyContent: 'center',
        gap: 12,
        maxWidth: window.innerWidth <= 600 ? '320px' : 'none',
        width: window.innerWidth <= 600 ? '100%' : 'auto'
      }}
    >
      {rightIcons.map((icon, idx) => (
        <div
          key={idx}
          style={{
            flex: window.innerWidth <= 600 ? '0 0 25%' : undefined,
            maxWidth: window.innerWidth <= 600 ? '25%' : undefined,
            display: 'flex',
            justifyContent: 'center'
          }}
        >
          {icon}
        </div>
      ))}
    </div>
  )
}
